<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import CatalogSourcesAPI from 'dashboard/api/catalogSources';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PageEmptyState from 'escaleshop/shared/components/PageEmptyState.vue';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
const DOT = '·';
const route = useRoute();
const router = useRouter();
const { isCloudFeatureEnabled, isOnChatwootCloud, accountId } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);
const isCatalogEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.CATALOG));

const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

const sources = ref([]);
const loading = ref(true);
const error = ref('');
const connectModalRef = ref(null);
let pollTimer = null;

const KIND_META = computed(() => ({
  shopify: {
    label: 'Shopify',
    icon: 'i-lucide-shopping-bag',
    available: true,
  },
  xml_feed: {
    label: t('CATALOG.SOURCES.KINDS.XML_FEED'),
    icon: 'i-lucide-rss',
    available: false,
  },
  manual: {
    label: t('CATALOG.SOURCES.KINDS.MANUAL'),
    icon: 'i-lucide-pencil',
    available: false,
  },
  upload: {
    label: t('CATALOG.SOURCES.KINDS.UPLOAD'),
    icon: 'i-lucide-file-spreadsheet',
    available: false,
  },
}));

const hasActiveSyncing = computed(() =>
  sources.value.some(s => ['pending', 'syncing'].includes(s.status))
);

const fetchSources = async () => {
  try {
    const { data } = await CatalogSourcesAPI.list();
    sources.value = data.sources || [];
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    loading.value = false;
  }
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = setInterval(async () => {
    await fetchSources();
    if (!hasActiveSyncing.value) stopPolling();
  }, 5000);
};

const stopPolling = () => {
  clearInterval(pollTimer);
  pollTimer = null;
};

onMounted(async () => {
  await fetchSources();
  if (hasActiveSyncing.value) startPolling();
});

onUnmounted(stopPolling);

const formatDate = ts =>
  ts
    ? new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(ts))
    : '—';

const statusColor = s =>
  ({
    active: 'bg-n-teal-5 text-n-teal-12',
    syncing: 'bg-n-amber-5 text-n-amber-12',
    pending: 'bg-n-amber-5 text-n-amber-12',
    error: 'bg-n-ruby-5 text-n-ruby-12',
    paused: 'bg-n-solid-3 text-n-slate-12',
  }[s] || 'bg-n-solid-3 text-n-slate-12');

const sourceStatusText = s => {
  switch (s) {
    case 'active':
      return t('CATALOG.SOURCES.STATUS.active');
    case 'syncing':
      return t('CATALOG.SOURCES.STATUS.syncing');
    case 'pending':
      return t('CATALOG.SOURCES.STATUS.pending');
    case 'error':
      return t('CATALOG.SOURCES.STATUS.error');
    case 'paused':
      return t('CATALOG.SOURCES.STATUS.paused');
    default:
      return s;
  }
};

const reimportSource = async source => {
  try {
    const { data } = await CatalogSourcesAPI.reimport(source.id);
    const idx = sources.value.findIndex(s => s.id === source.id);
    if (idx >= 0) sources.value[idx] = data;
    startPolling();
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  }
};

const disconnectSource = async source => {
  if (!window.confirm(t('CATALOG.SOURCES.CONFIRM_DISCONNECT'))) return;
  try {
    await CatalogSourcesAPI.disconnect(source.id);
    await fetchSources();
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  }
};

const connectShopify = () => {
  const accountId = route.params.accountId;
  connectModalRef.value?.close();
  window.location.href = `/app/accounts/${accountId}/settings/integrations/shopify`;
};

const kindMeta = kind =>
  KIND_META.value[kind] || { label: kind, icon: 'i-lucide-package' };
</script>

<template>
  <section class="flex flex-col w-full h-full bg-n-solid-2">
    <template v-if="!isCatalogEnabled">
      <div class="flex flex-1 items-center justify-center min-h-[400px] px-6 py-12">
        <BasePaywallModal
          feature-prefix="CATALOG"
          :i18n-key="paywallI18nKey"
          :is-super-admin="isSuperAdmin"
          :is-on-chatwoot-cloud="isOnChatwootCloud"
          @upgrade="openBilling"
        />
      </div>
    </template>
    <template v-else>
    <div
      class="flex-1 overflow-y-auto px-6 lg:px-16 scrollbar-custom flex justify-center"
    >
      <div class="w-full max-w-7xl py-6 flex flex-col gap-6">
        <header class="flex items-center justify-between gap-4">
          <div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('CATALOG.SOURCES.TITLE') }}
            </h1>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ t('CATALOG.SOURCES.SUBTITLE') }}
            </p>
          </div>
          <Button
            solid
            blue
            icon="i-lucide-plus"
            :label="t('CATALOG.SOURCES.CONNECT_NEW')"
            @click="connectModalRef?.open()"
          />
        </header>

        <div v-if="loading" class="flex items-center justify-center py-20">
          <Spinner />
        </div>

        <div
          v-else-if="error"
          class="px-4 py-3 rounded-md bg-n-ruby-3 text-sm text-n-ruby-12"
        >
          {{ error }}
        </div>

        <PageEmptyState
          v-else-if="!sources.length"
          icon="i-lucide-plug"
          :title="t('CATALOG.SOURCES.EMPTY_TITLE')"
          :description="t('CATALOG.SOURCES.EMPTY_DESCRIPTION')"
        >
          <Button
            solid
            blue
            icon="i-lucide-plus"
            :label="t('CATALOG.SOURCES.CONNECT_NEW')"
            @click="connectModalRef?.open()"
          />
        </PageEmptyState>

        <div v-else class="space-y-3">
          <div
            v-for="source in sources"
            :key="source.id"
            class="flex items-start gap-4 px-4 py-4 bg-n-solid-1 border border-n-weak rounded-lg"
          >
            <div
              class="size-10 rounded-md bg-n-solid-3 flex items-center justify-center shrink-0"
            >
              <i :class="kindMeta(source.kind).icon" class="text-n-slate-11 text-lg" />
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ kindMeta(source.kind).label }}
                </span>
                <span v-if="source.name" class="text-sm text-n-slate-11">{{ DOT }}</span>
                <span v-if="source.name" class="text-sm text-n-slate-11">
                  {{ source.name }}
                </span>
                <span
                  :class="statusColor(source.status)"
                  class="text-[10px] px-1.5 py-0.5 rounded ml-1 flex items-center gap-1"
                >
                  <i
                    v-if="['pending', 'syncing'].includes(source.status)"
                    class="i-lucide-loader-2 animate-spin text-[10px]"
                  />
                  {{ sourceStatusText(source.status) }}
                </span>
              </div>
              <p v-if="source.reference" class="text-xs text-n-slate-11 mt-0.5">
                {{ source.reference }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{
                  t('CATALOG.SOURCES.SYNC_SUMMARY', {
                    date: formatDate(source.last_synced_at),
                    products: source.products_count,
                    variants: source.variants_count,
                  })
                }}
              </p>
              <p
                v-if="source.last_error"
                class="text-xs text-n-ruby-11 mt-1 truncate"
              >
                {{ source.last_error }}
              </p>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <Button
                ghost
                slate
                sm
                icon="i-lucide-refresh-cw"
                :label="t('CATALOG.SOURCES.REIMPORT')"
                :disabled="['pending', 'syncing'].includes(source.status)"
                @click="reimportSource(source)"
              />
              <Button
                ghost
                ruby
                sm
                icon="i-lucide-unlink"
                :label="t('CATALOG.SOURCES.DISCONNECT')"
                @click="disconnectSource(source)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="connectModalRef"
      :title="t('CATALOG.SOURCES.CONNECT_NEW')"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="(meta, kind) in KIND_META"
          :key="kind"
          class="flex items-center gap-3 px-4 py-3 rounded-md border border-n-weak hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!meta.available"
          @click="kind === 'shopify' ? connectShopify() : null"
        >
          <i :class="meta.icon" class="text-n-slate-11 text-lg" />
          <span class="text-sm font-medium text-n-slate-12 flex-1 text-left">
            {{ meta.label }}
          </span>
          <span
            v-if="!meta.available"
            class="text-[10px] px-1.5 py-0.5 rounded bg-n-amber-3 text-n-amber-12"
          >
            {{ t('CATALOG.SOURCES.COMING_SOON') }}
          </span>
        </button>
      </div>
    </Dialog>
    </template>
  </section>
</template>
